// useTraceShellWork - the shell door's ops for the open trace.
//
// Same shape and the same reasoning as useTraceCapability: a parse, not a model
// call, so it runs by itself and is gated on the trace having shell calls at
// all. What it returns are lane items, which reach the model through the same
// `extraItems` seam the reading's verified additions use.
//
// It is deliberately NOT folded into `buildSemanticFeed` with the other
// extractors, even though that is where it conceptually belongs. The feed is
// synchronous and this needs a wasm parser, so folding it in would make the
// whole model async and every consumer of it await. Riding `extraItems`
// instead keeps the ops OUT of the feed's claim and coverage accounting, which
// is the honest place for them: the residual gauge counts what the committed
// extractors claimed, and a grammar-derived read is a different tier of
// knowledge - the same distinction the verified-addition tier already draws.
import { useEffect, useMemo, useRef, useState } from "react";
import { readShellWork, shellWorkSummaryLine } from "./traceShellWork.js";
import { actionShellCalls, buildTraceActions } from "./traceActions.js";

const IDLE = { status: "idle", items: [], result: null, calls: 0 };

// `actions` is the caller's table for these turns, when it already built one.
export function useTraceShellWork(turns, actions = null) {
  const [state, setState] = useState(IDLE);
  const table = useMemo(() => actions || (turns ? buildTraceActions(turns) : null), [actions, turns]);
  const callCount = useMemo(() => (table ? actionShellCalls(table).calls.length : 0), [table]);
  const latest = useRef(0);

  useEffect(() => {
    if (!callCount) {
      setState(IDLE);
      return undefined;
    }
    const run = latest.current + 1;
    latest.current = run;
    const controller = new AbortController();
    const settle = (result, status) => {
      if (controller.signal.aborted || latest.current !== run) return;
      setState({ status, items: result.items, result, calls: callCount });
    };
    setState({ status: "running", items: [], result: null, calls: callCount });
    readShellWork(turns, { signal: controller.signal, onProgress: (partial) => settle(partial, "running"), actions: table })
      .then((result) => settle(result, "ready"))
      .catch(() => {
        if (controller.signal.aborted || latest.current !== run) return;
        // A failed parse pass costs the shell ops, not the timeline.
        setState({ status: "error", items: [], result: null, calls: callCount });
      });
    return () => controller.abort();
  }, [turns, table, callCount]);

  return state;
}

export { shellWorkSummaryLine };
